import styled from '@emotion/styled';
import Menu from '@mui/material/Menu';
import { MenuItemProps } from '@mui/material/MenuItem';
import * as React from 'react';
import { ReactNode } from 'react';

const StyledWrapper = styled.div`
    display: flex;
    justify-content: space-between !important;
    border-radius: 12px;

    & > svg {
        margin-left: 32px;
    }
`;

const LabelContainer = styled.div`
    border-radius: 12px;
    width: 100%;
`;

const StyledMenu = styled(Menu)`
    pointer-events: none;
`;

const ItemsContainer = styled.div`
    pointer-events: auto;
    outline: none;
`;

interface Props extends MenuItemProps {
    parentMenuOpen?: boolean;
    label: ReactNode;
    children?: any;
    menu?: ReactNode[];
}

export const NestedMenuItem = ({ parentMenuOpen, label, children }: Props) => {
    const menuItemRef = React.useRef<HTMLDivElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const menuContainerRef = React.useRef<HTMLDivElement>(null);

    const [anchorOriginHorizontal, setAnchorOriginHorizontal] = React.useState<'right' | 'left'>('right');
    const [transformOriginHorizontal, setTransformOriginHorizontal] = React.useState<'right' | 'left'>('left');

    const [isSubMenuOpen, setIsSubMenuOpen] = React.useState(false);

    const handleMouseEnter = () => {
        setIsSubMenuOpen(true);
    };

    const handleMouseLeave = () => {
        setIsSubMenuOpen(false);
    };

    const open = isSubMenuOpen && parentMenuOpen;

    const handleCalculatePosition = () => {
        const triggerRect = menuItemRef.current?.getBoundingClientRect();
        const submenuRect = menuContainerRef.current?.getBoundingClientRect();

        const anchorRightEdge = (triggerRect?.x || 0) + (triggerRect?.width || 0);
        const submenuRightEdge = anchorRightEdge + (submenuRect?.width || 0);

        if (window.innerWidth > submenuRightEdge && anchorRightEdge !== submenuRightEdge) {
            setAnchorOriginHorizontal('right');
            setTransformOriginHorizontal('left');
        } else {
            setAnchorOriginHorizontal('left');
            setTransformOriginHorizontal('right');
        }
    };

    return (
        <StyledWrapper
            ref={containerRef}
            onMouseEnter={handleMouseEnter}
            onMouseOver={handleCalculatePosition}
            onMouseLeave={handleMouseLeave}>
            <LabelContainer ref={menuItemRef}>{label}</LabelContainer>
            <StyledMenu
                slotProps={{
                    paper: { style: { borderRadius: '12px', margin: transformOriginHorizontal === 'left' ? '0 0 0 12px' : '0 0 12px 0' } },
                }}
                hideBackdrop
                anchorEl={menuItemRef.current}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: anchorOriginHorizontal,
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: transformOriginHorizontal,
                }}
                open={!!open}
                autoFocus={false}
                disableAutoFocus
                disableEnforceFocus
                onClose={() => setIsSubMenuOpen(false)}>
                <ItemsContainer ref={menuContainerRef}>{children}</ItemsContainer>
            </StyledMenu>
        </StyledWrapper>
    );
};
